import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/App';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { usePhotoPreview } from '@/components/PhotoPreview';
import { StandingMovement } from '@/components/StandingMovement';
import { SeasonRecapDialog } from '@/components/SeasonRecapDialog';

export default function PlayerStatsPage({ user: propUser, onLogout }) {
  const previewPhoto = usePhotoPreview();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(propUser);
  const [stats, setStats] = useState(null);
  const [games, setGames] = useState([]);
  const [mvpCount, setMvpCount] = useState(0);
  const [latestGameRating, setLatestGameRating] = useState(null);
  const [ratingHistory, setRatingHistory] = useState([]);
  const [showRecap, setShowRecap] = useState(false);
  const [standing, setStanding] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      // Get current user if not provided
      if (!user) {
        const userResponse = await api.get('/auth/me');
        setUser(userResponse.data);
      }

      const userId = user?.id || (await api.get('/auth/me')).data.id;

      // Get community stats
      const statsResponse = await api.get(`/users/${userId}/community-stats`);
      setStats(statsResponse.data);

      // Get community standing (rank by effective rating)
      try {
        const standingResponse = await api.get(`/users/${userId}/standing`);
        setStanding(standingResponse.data);
      } catch (error) {
        console.error('Failed to fetch standing:', error);
      }

      // Get all games to find user's games and MVP count
      const gamesResponse = await api.get('/games');
      const allGames = gamesResponse.data;
      
      // Filter games where user participated
      const userGames = allGames.filter(game => 
        game.registered_players && game.registered_players.includes(userId)
      );
      
      // Sort by date descending — show ALL games the player participated in
      userGames.sort((a, b) => new Date(b.date) - new Date(a.date));
      const recentGames = userGames;
      
      // Get ratings for recent games
      const gamesWithRatings = await Promise.all(
        recentGames.map(async (game) => {
          try {
            const summaryResponse = await api.get(`/games/${game.id}/ratings-summary`);
            const playerAverage = summaryResponse.data.player_averages[userId];
            return {
              ...game,
              myRating: playerAverage ? playerAverage.average : null,
              ratingsCount: playerAverage ? playerAverage.count : 0
            };
          } catch (error) {
            return { ...game, myRating: null, ratingsCount: 0 };
          }
        })
      );
      
      setGames(gamesWithRatings);
      
      // Get latest game rating (most recent game where user was rated)
      const latestWithRating = gamesWithRatings.find(g => g.myRating !== null);
      if (latestWithRating) {
        setLatestGameRating(latestWithRating.myRating);
      }
      
      // Count MVP wins
      const mvpWins = userGames.filter(game => game.mvp_player_id === userId).length;
      setMvpCount(mvpWins);

      // Get rating history
      try {
        const historyResponse = await api.get(`/users/${userId}/rating-history`);
        // Reverse to show oldest first (left to right progression)
        const historyData = historyResponse.data.history.reverse().map((game) => ({
          name: game.game_date, // Show date instead of game name
          rating: game.rating,
          fullName: game.game_title,
          date: game.game_date
        }));
        setRatingHistory(historyData);
      } catch (error) {
        console.error('Failed to fetch rating history:', error);
      }

    } catch (error) {
      toast.error('Failed to load stats');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getKudosIcon = (type) => {
    const icons = {
      teamwork: '🤝',
      positive_energy: '😊',
      supportive: '💪',
      great_spike: '🏐',
      great_serve: '💥',
      great_pass: '🎯',
      great_defense: '🛡️',
      strong_block: '🚫',
      great_setting: '🙌',
      // Legacy types for backward compatibility
      friendly: '😊',
      performance: '⚡',
      communication: '💬'
    };
    return icons[type] || '⭐';
  };

  const getKudosColor = (type) => {
    const colors = {
      teamwork: 'bg-blue-100 text-blue-700',
      positive_energy: 'bg-yellow-100 text-yellow-700',
      supportive: 'bg-orange-100 text-orange-700',
      great_spike: 'bg-red-100 text-red-700',
      great_serve: 'bg-purple-100 text-purple-700',
      great_pass: 'bg-green-100 text-green-700',
      great_defense: 'bg-indigo-100 text-indigo-700',
      strong_block: 'bg-pink-100 text-pink-700',
      great_setting: 'bg-teal-100 text-teal-700',
      // Legacy types for backward compatibility
      friendly: 'bg-yellow-100 text-yellow-700',
      performance: 'bg-purple-100 text-purple-700',
      communication: 'bg-green-100 text-green-700'
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-xl text-gray-600">Loading your stats...</div>
          </div>
        </div>
      </div>
    );
  }

  const participationDisabled = stats?.hidden === true;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <Navbar user={user} onLogout={onLogout} />
      <SeasonRecapDialog open={showRecap} onClose={() => setShowRecap(false)} />
      
      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-8 max-w-6xl">
        {/* Hero Section */}
        <div className="mb-6 sm:mb-8">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 text-white shadow-2xl">
            <div className="flex flex-col md:flex-row items-center gap-4 sm:gap-6">
              {/* Profile Photo */}
              <div className="relative">
                {user?.profile_photo_url ? (
                  <img
                    src={user.profile_photo_url}
                    alt={user.first_name}
                    onClick={() => previewPhoto(user.profile_photo_url, `${user.first_name || ''} ${user.last_name || ''}`.trim())}
                    className="w-24 h-24 sm:w-32 sm:h-32 rounded-full border-4 border-white shadow-xl object-cover cursor-zoom-in"
                  />
                ) : (
                  <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full border-4 border-white shadow-xl bg-white/20 flex items-center justify-center">
                    <span className="text-4xl sm:text-5xl">{user?.first_name?.charAt(0)}</span>
                  </div>
                )}
                {mvpCount > 0 && (
                  <div className="absolute -bottom-2 -right-2 bg-yellow-400 text-yellow-900 rounded-full px-2 sm:px-3 py-1 text-xs sm:text-sm font-bold shadow-lg">
                    🏆 {mvpCount}
                  </div>
                )}
              </div>

              {/* User Info */}
              <div className="flex-1 text-center md:text-left">
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2">
                  {user?.first_name} {user?.last_name}
                </h1>
                <p className="text-blue-100 text-base sm:text-lg mb-3 sm:mb-4">
                  Player Dashboard
                  <button
                    onClick={() => setShowRecap(true)}
                    data-testid="open-season-recap-btn"
                    className="ml-3 inline-flex items-center gap-1 bg-white/20 hover:bg-white/30 text-white text-xs sm:text-sm font-semibold px-3 py-1.5 rounded-full transition-colors align-middle"
                  >
                    🎉 Season Recap
                  </button>
                </p>
                
                {/* Quick Stats */}
                <div className="flex flex-wrap gap-2 sm:gap-4 justify-center md:justify-start">
                  <div className="bg-white/20 backdrop-blur rounded-lg px-3 sm:px-4 py-2">
                    <div className="text-2xl sm:text-3xl font-bold">{games.length}</div>
                    <div className="text-xs sm:text-sm text-blue-100">Games Played</div>
                  </div>
                  {!participationDisabled && (
                    <>
                      <div className="bg-white/20 backdrop-blur rounded-lg px-3 sm:px-4 py-2">
                        <div className="text-2xl sm:text-3xl font-bold">
                          {stats?.community_rating?.average?.toFixed(2) || '0.0'}
                        </div>
                        <div className="text-xs sm:text-sm text-blue-100">Community Rating</div>
                      </div>
                      <div className="bg-white/20 backdrop-blur rounded-lg px-3 sm:px-4 py-2">
                        <div className="text-2xl sm:text-3xl font-bold">{stats?.kudos?.total || 0}</div>
                        <div className="text-xs sm:text-sm text-blue-100">Total Kudos</div>
                      </div>
                    </>
                  )}
                  <div className="bg-yellow-400/30 backdrop-blur rounded-lg px-3 sm:px-4 py-2 border-2 border-yellow-300">
                    <div className="text-2xl sm:text-3xl font-bold">{mvpCount}</div>
                    <div className="text-xs sm:text-sm text-yellow-100">MVP Wins 🏆</div>
                  </div>
                  {!participationDisabled && (
                  <div className="bg-white/20 backdrop-blur rounded-lg px-3 sm:px-4 py-2" data-testid="stats-standing">
                    <div className="text-2xl sm:text-3xl font-bold flex items-center justify-center gap-1.5">
                      {standing?.rank ? standing.rank_label : 'Unrated'}
                      <StandingMovement standing={standing} className="!text-white/90" />
                    </div>
                    <div className="text-xs sm:text-sm text-blue-100">{standing?.rank ? `of ${standing.total} players` : 'Standing'}</div>
                  </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {participationDisabled && (
          <Card className="shadow-lg mb-6" data-testid="stats-participation-disabled">
            <CardContent className="p-6 sm:p-8 text-center space-y-2">
              <div className="text-4xl">🔒</div>
              <h3 className="text-lg font-bold text-gray-900">Rating participation is disabled</h3>
              <p className="text-sm text-gray-600">
                Your rating and kudos are hidden and kept privately, visible only to admins.
                Re-enable rating participation in <a href="/settings" className="underline font-semibold text-orange-600">Settings</a> to see them again.
              </p>
            </CardContent>
          </Card>
        )}

        {!participationDisabled && (
        <div className="grid md:grid-cols-2 gap-4 sm:gap-6 mb-6">
          {/* Community Rating Card */}
          <Card className="shadow-lg hover:shadow-xl transition-shadow">
            <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 sm:p-6">
              <CardTitle className="flex items-center gap-2 text-xl sm:text-2xl">
                <span className="text-xl sm:text-2xl">⭐</span>
                Community Rating
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 sm:pt-6 px-4 sm:px-6">
              <div className="text-center mb-4 sm:mb-6">
                <div className="text-4xl sm:text-5xl md:text-6xl font-bold text-green-600 mb-2">
                  {stats?.community_rating?.average?.toFixed(2) || '0.0'}
                </div>
                <div className="text-sm sm:text-base text-gray-600">out of 10.0</div>
                <div className="text-xs sm:text-sm text-gray-500 mt-2">
                  Based on {stats?.community_rating?.total_games_rated || 0} games
                </div>
              </div>

              {/* Rating Comparison */}
              <div className="space-y-3">
                <div className="flex justify-between items-center p-2 sm:p-3 bg-gray-50 rounded-lg">
                  <span className="text-xs sm:text-sm font-medium text-gray-700">Community Rating</span>
                  <Badge className="bg-green-500 text-xs sm:text-sm">{stats?.community_rating?.average?.toFixed(2) || '0.0'}</Badge>
                </div>
                {stats?.admin_rating > 0 && (
                  <div className="flex justify-between items-center p-2 sm:p-3 bg-gray-50 rounded-lg">
                    <span className="text-xs sm:text-sm font-medium text-gray-700">Admin Rating</span>
                    <Badge className="bg-blue-500 text-xs sm:text-sm">{stats?.admin_rating?.toFixed(2)}</Badge>
                  </div>
                )}
              </div>

              {/* Rating Progress Chart */}
              {ratingHistory.length > 0 && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h4 className="text-base font-bold text-green-900 mb-2">Rating Progress</h4>
                  <p className="text-xs text-gray-600 mb-4">Your performance over the last {ratingHistory.length} game{ratingHistory.length > 1 ? 's' : ''}</p>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={ratingHistory} margin={{ top: 30, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        dataKey="name" 
                        tick={{ fontSize: 11 }}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                      />
                      <YAxis 
                        domain={[0, 10]} 
                        ticks={[0, 2, 4, 6, 8, 10]}
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip 
                        content={({ active, payload }) => {
                          if (active && payload && payload[0]) {
                            const data = payload[0].payload;
                            return (
                              <div className="bg-white p-3 border-2 border-green-200 rounded-lg shadow-lg">
                                <p className="font-bold text-green-900">{data.fullName}</p>
                                <p className="text-sm text-gray-600">{data.date}</p>
                                <p className="text-lg font-bold text-green-700 mt-1">Rating: {data.rating}/10</p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="rating" 
                        stroke="#15803d" 
                        strokeWidth={3}
                        dot={{ fill: '#15803d', r: 6 }}
                        activeDot={{ r: 8 }}
                        label={({ value, x, y }) => (
                          <text 
                            x={x} 
                            y={y - 10} 
                            fill="#15803d" 
                            fontSize="14" 
                            fontWeight="bold" 
                            textAnchor="middle"
                          >
                            {value}
                          </text>
                        )}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Kudos Card */}
          <Card className="shadow-lg hover:shadow-xl transition-shadow">
            <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 sm:p-6">
              <CardTitle className="flex items-center gap-2 text-xl sm:text-2xl">
                <span className="text-xl sm:text-2xl">💝</span>
                Kudos Received
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 sm:pt-6 px-4 sm:px-6">
              <div className="text-center mb-4 sm:mb-6">
                <div className="text-4xl sm:text-5xl md:text-6xl font-bold text-purple-600 mb-2">
                  {stats?.kudos?.total || 0}
                </div>
                <div className="text-sm sm:text-base text-gray-600">Total Kudos</div>
              </div>

              {/* Kudos Breakdown */}
              <div className="space-y-2 sm:space-y-3">
                {Object.entries(stats?.kudos?.breakdown || {}).map(([type, count]) => (
                  count > 0 && (
                    <div key={type} className="flex items-center justify-between p-2 sm:p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <div className="flex items-center gap-2">
                        <span className="text-xl sm:text-2xl">{getKudosIcon(type)}</span>
                        <span className="text-sm sm:text-base font-medium capitalize">{type}</span>
                      </div>
                      <Badge className={`${getKudosColor(type)} text-xs sm:text-sm`}>{count}</Badge>
                    </div>
                  )
                ))}
                {(!stats?.kudos?.total || stats.kudos.total === 0) && (
                  <div className="text-center py-8 text-sm sm:text-base text-gray-500">
                    No kudos received yet. Keep playing!
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
        )}

        {/* Games Played */}
        <Card className="shadow-lg">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 sm:p-6">
            <CardTitle className="text-xl sm:text-2xl">Games Played</CardTitle>
          </CardHeader>
          <CardContent className="p-3 sm:p-6">
            {games.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-gray-200">
                      <th className="text-left p-2 sm:p-3 text-xs sm:text-sm font-semibold text-gray-700">Date</th>
                      <th className="text-left p-2 sm:p-3 text-xs sm:text-sm font-semibold text-gray-700">Game</th>
                      <th className="text-center p-2 sm:p-3 text-xs sm:text-sm font-semibold text-gray-700">Your Rating</th>
                      <th className="text-center p-2 sm:p-3 text-xs sm:text-sm font-semibold text-gray-700">Ratings</th>
                      <th className="text-center p-2 sm:p-3 text-xs sm:text-sm font-semibold text-gray-700">MVP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {games.map((game) => (
                      <tr key={game.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="p-2 sm:p-3 text-xs sm:text-sm text-gray-600">{game.date}</td>
                        <td className="p-2 sm:p-3">
                          <div className="text-xs sm:text-sm font-medium text-gray-900">{game.title}</div>
                          <div className="text-xs text-gray-500">{game.location}</div>
                        </td>
                        <td className="p-2 sm:p-3 text-center">
                          {game.myRating ? (
                            <Badge className="bg-green-500 text-xs sm:text-sm">
                              {game.myRating.toFixed(2)}/10
                            </Badge>
                          ) : (
                            <span className="text-xs sm:text-sm text-gray-400">Not rated</span>
                          )}
                        </td>
                        <td className="p-2 sm:p-3 text-center text-xs sm:text-sm text-gray-600">{game.ratingsCount}</td>
                        <td className="p-2 sm:p-3 text-center">
                          {game.mvp_player_id === user?.id && (
                            <span className="text-lg sm:text-xl">🏆</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12 text-sm sm:text-base text-gray-500">
                No games played yet. Join your first game!
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
